import {
  ChangeEvent,
  DragEvent,
  FC,
  ReactNode,
  useCallback,
  useState
} from "react"
import {
  Image as ImageIcon,
  X as CloseIcon,
  Trash2 as TrashIcon
} from "lucide-react"
import { useAppDispatch } from "../../hooks"
import bbApi from "../../../api"
import { pushToast } from "../../../redux/slices/notifications"
import DropZone from "../drop-zone"
import { formatMessage } from "../../utils"
import If from "../if"
import CropModal from "./crop-modal"
import { getImageDimensions } from "./crop-image"

const MAX_SIZE_BYTES = 5 * 1024 * 1024
const SUPPORTED_TYPES = ["image/jpeg", "image/png"]

interface Props {
  className?: string
  buttonLabel?: string
  imageUrl: string
  droppable?: boolean
  placeholder?: ReactNode
  isFullRounded?: boolean
  useS3?: boolean
  /** When true, selecting a file opens a crop/reposition modal before upload. */
  enableCrop?: boolean
  /** Shape of the crop frame when cropping is enabled. */
  cropShape?: "round" | "rect"
  /** Square output size in px for the cropped image. */
  cropOutputSize?: number
  /** Maximum upload size in bytes. Defaults to 5MB. */
  maxSizeBytes?: number
  /** Minimum required image width in px. When set, images smaller than this are rejected. */
  minWidth?: number
  /** Minimum required image height in px. When set, images smaller than this are rejected. */
  minHeight?: number
  /** Short hint rendered under the choose-file button, e.g. "Max 2 MB, 200x200 min". */
  sizeHint?: ReactNode
  /** Error message shown when a file fails the size or dimension checks. */
  requirementsErrorMessage?: string
  onChangeImageUrl: (url: string) => void
  onRemoveImage?: () => void
}

const ImageUploader: FC<Props> = ({
  className = "",
  buttonLabel = "Choose File",
  imageUrl,
  droppable,
  placeholder,
  isFullRounded = true,
  useS3 = true,
  enableCrop = false,
  cropShape = "round",
  cropOutputSize = 512,
  maxSizeBytes = MAX_SIZE_BYTES,
  minWidth,
  minHeight,
  sizeHint,
  requirementsErrorMessage,
  onChangeImageUrl,
  onRemoveImage
}) => {
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropFileName, setCropFileName] = useState("profile.jpg")

  const validateFile = useCallback(
    async (file: File): Promise<boolean> => {
      if (file.size > maxSizeBytes) {
        dispatch(
          pushToast({
            msg: requirementsErrorMessage || "File size must be less than 5MB.",
            theme: "danger",
            timeout: 5000
          })
        )
        return false
      }

      if (!SUPPORTED_TYPES.includes(file.type)) {
        dispatch(
          pushToast({
            msg: "Unsupported file type. Please upload a JPG or PNG image.",
            theme: "danger",
            timeout: 3000
          })
        )
        return false
      }

      if (minWidth || minHeight) {
        let width = 0
        let height = 0
        try {
          ;({ width, height } = await getImageDimensions(file))
        } catch (error) {
          dispatch(
            pushToast({
              msg: "Could not read image. Please try a different file.",
              theme: "danger",
              timeout: 3000
            })
          )
          return false
        }

        if (
          (minWidth && width < minWidth) ||
          (minHeight && height < minHeight)
        ) {
          dispatch(
            pushToast({
              msg:
                requirementsErrorMessage ||
                "This image doesn't meet the requirements.",
              theme: "danger",
              timeout: 5000
            })
          )
          return false
        }
      }

      return true
    },
    [dispatch, maxSizeBytes, minWidth, minHeight, requirementsErrorMessage]
  )

  const handleSelectFile = useCallback(
    async (file: File) => {
      if (!(await validateFile(file))) return

      if (enableCrop) {
        setCropFileName(file.name)
        setCropSrc(URL.createObjectURL(file))
        return
      }

      uploadPicture(file)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateFile, enableCrop]
  )

  const closeCropper = useCallback(() => {
    setCropSrc(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
  }, [])

  const handleCropped = useCallback(
    (file: File) => {
      closeCropper()
      uploadPicture(file)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeCropper]
  )

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleSelectFile(e.target.files[0])
    }
    // Reset so selecting the same file again re-triggers onChange.
    e.target.value = ""
  }

  const onChooseFile = (e: DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.files[0]) {
      handleSelectFile(e.dataTransfer.files[0])
    }
  }

  const removeBgImage = () => {
    onRemoveImage?.()
    onChangeImageUrl("")
  }

  const uploadPicture = useCallback(
    async (file: File) => {
      try {
        setLoading(true)
        const data = await bbApi.asset.uploadImage(file, file.name, useS3)

        if ("status" in data) {
          dispatch(
            pushToast({
              msg:
                formatMessage(data.message) ||
                "Failed to upload file, please try again later.",
              timeout: 3000
            })
          )
        } else {
          onChangeImageUrl(data.url)
        }
      } catch (error) {
        dispatch(
          pushToast({
            msg: "Failed to upload file, please try again later.",
            theme: "danger",
            timeout: 3000
          })
        )
      } finally {
        setLoading(false)
      }
    },
    [dispatch, onChangeImageUrl, useS3]
  )

  const cropper = cropSrc ? (
    <CropModal
      show={!!cropSrc}
      imageSrc={cropSrc}
      fileName={cropFileName}
      cropShape={cropShape}
      outputSize={cropOutputSize}
      onCancel={closeCropper}
      onCropped={handleCropped}
    />
  ) : null

  if (droppable) {
    return (
      <>
        <DropZone noDropCover onDrop={onChooseFile}>
          <div className={`${className} w-full`}>
            {imageUrl ? (
              <div className="h-full">
                <div
                  className="relative cursor-pointer"
                  onClick={removeBgImage}
                >
                  <CloseIcon className="absolute top-2 right-2" />
                </div>
                <img className="max-h-full m-auto" src={imageUrl} alt="" />
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center">
                <ImageIcon width={30} height={30} />
                <div className="py-3">Drag and drop file here or</div>

                <div className="text-center">
                  {loading ? (
                    <div className="text-sm border border-neutral-300 rounded-md overflow-hidden px-2 py-1 bg-neutral-100 text-neutral-400">
                      Processing...
                    </div>
                  ) : (
                    <label className="cursor-pointer text-sm border border-neutral-300 rounded-md overflow-hidden px-2 py-1 bg-neutral-100">
                      {buttonLabel}
                      <input
                        name="file"
                        type="file"
                        hidden
                        accept="image/jpeg,image/png"
                        onChange={handleChangeInput}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        </DropZone>
        {cropper}
      </>
    )
  }

  return (
    <>
      <div className={`${className} w-full flex items-center`}>
        <div
          className={`flex flex-col items-center justify-center overflow-hidden ${
            isFullRounded
              ? "rounded-full w-24 h-24 border border-gray-200"
              : "w-40 h-24 border border-gray-200 rounded-lg"
          }`}
        >
          <If
            condition={!!imageUrl}
            then={
              <img
                src={imageUrl}
                className={
                  isFullRounded
                    ? "w-24 h-24 rounded-full object-cover truncate"
                    : "w-40 h-24 object-contain"
                }
                alt=""
              />
            }
            else={
              placeholder || (
                <span
                  className={`${
                    isFullRounded ? "" : "w-24 h-24"
                  } text-center text-sm inline-flex items-center justify-center`}
                >
                  No file selected
                </span>
              )
            }
          />
        </div>

        <div className="ml-5">
          <div className="flex items-center w-full">
            {loading ? (
              <div className="h-11 flex-1 flex items-center justify-center text-sm border border-gray-200 rounded-md overflow-hidden px-4 bg-white text-neutral-400">
                Processing...
              </div>
            ) : (
              <label className="h-11 flex-1 flex items-center justify-center cursor-pointer text-sm border border-gray-200 rounded-md overflow-hidden px-4 bg-white">
                {buttonLabel}
                <input
                  name="file"
                  type="file"
                  hidden
                  accept="image/jpeg,image/png"
                  onChange={handleChangeInput}
                />
              </label>
            )}
            {imageUrl && !loading && (
              <div
                className="h-11 w-11 flex-shrink-0 ml-3 flex items-center justify-center rounded-md border border-red-200 bg-red-100 cursor-pointer"
                onClick={removeBgImage}
              >
                <TrashIcon size={16} />
              </div>
            )}
          </div>
          {sizeHint && (
            <div className="mt-1 text-sm text-neutral-500 whitespace-nowrap">
              {sizeHint}
            </div>
          )}
        </div>
      </div>
      {cropper}
    </>
  )
}

export default ImageUploader
